import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Minus, Plus, Download, Truck } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { CartDrawer } from "@/components/CartDrawer";
import { Footer } from "@/components/Footer";
import { useCart } from "@/context/CartContext";
import { api, euro } from "@/lib/api";

export default function ProductPage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [qty, setQty] = useState(1);
  const { add, setOpen } = useCart();

  useEffect(() => {
    setP(null);
    setErr(false);
    api.get(`/products/${id}`).then((r) => setP(r.data)).catch(() => setErr(true));
  }, [id]);

  const onAdd = () => {
    add(p, qty);
    toast.success(`${p.name} × ${qty} aggiunto`);
    setOpen(true);
  };

  return (
    <main data-testid="product-page">
      <Navbar />
      <CartDrawer />
      <section className="px-5 pb-24 pt-28 md:px-10">
        <div className="mx-auto max-w-[1400px]">
          <Link to="/" data-testid="back-to-shop-link" className="mb-10 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#a3b1aa] hover:text-[#c4dcca]">
            <ArrowLeft size={14} /> Torna alla home
          </Link>
          {err && <p data-testid="product-not-found" className="text-[#a3b1aa]">Prodotto non trovato.</p>}
          {p && (
            <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
              <motion.div initial={{ clipPath: "inset(0 0 100% 0)" }} animate={{ clipPath: "inset(0 0 0% 0)" }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                className="relative">
                <div className="absolute -inset-6 bg-[#98bfa2]/15 blur-[80px]" />
                <div className="clip-frame relative aspect-square overflow-hidden border border-[rgba(152,191,162,0.25)]">
                  <img src={p.image} alt={p.name} className="h-full w-full object-cover" data-testid="product-image" />
                </div>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }} className="flex flex-col justify-center">
                <p className="eyebrow mb-4">{p.category} {p.badge && `· ${p.badge}`}</p>
                <h1 data-testid="product-name" className="font-display text-5xl font-900 uppercase leading-[0.9] md:text-7xl">{p.name}</h1>
                {!p.coming_soon && <p data-testid="product-price" className="mt-6 font-display text-4xl font-800 text-[#c4dcca]">{euro(p.price)}</p>}
                <p className="mt-6 max-w-lg text-base leading-relaxed text-[#a3b1aa] md:text-lg">{p.description}</p>
                {!p.coming_soon && (
                <div className="mt-8 flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-[#a3b1aa]">
                  {p.is_digital ? <Download size={16} className="text-[#98bfa2]" /> : <Truck size={16} className="text-[#98bfa2]" />}
                  <span data-testid="product-availability">
                    {p.is_digital ? "Download immediato dopo l'acquisto" : p.stock > 0 ? `Disponibile · ${p.stock} pezzi` : "Esaurito"}
                  </span>
                </div>
                )}
                {p.coming_soon ? (
                  <div data-testid="product-coming-soon" className="mt-10 border border-[#98bfa2]/40 bg-[#0b120e] p-6">
                    <p className="font-display text-2xl font-800 uppercase text-[#c4dcca]">In fase di elaborazione</p>
                    <p className="mt-2 text-sm leading-relaxed text-[#a3b1aa] md:text-base">Stiamo completando l'app: sarà disponibile a breve e riservata ai clienti premium di Hybrid Health Lab.</p>
                    <Link to="/training-online" data-testid="product-coming-soon-link" className="mt-4 inline-block font-mono text-xs uppercase tracking-widest text-[#98bfa2] underline hover:text-[#c4dcca]">Scopri il training online</Link>
                  </div>
                ) : (
                <div className="mt-10 flex flex-wrap gap-3">
                  <div className="flex items-center gap-5 border border-white/15 px-5">
                    <button data-testid="qty-decrease" onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Meno"><Minus size={16} /></button>
                    <span data-testid="qty-value" className="w-6 text-center font-mono">{qty}</span>
                    <button data-testid="qty-increase" onClick={() => setQty((q) => Math.min(50, q + 1))} aria-label="Più"><Plus size={16} /></button>
                  </div>
                  <button data-testid="add-to-cart-btn" disabled={!p.is_digital && p.stock < 1} onClick={onAdd}
                    className="flex-1 bg-[#98bfa2] px-8 py-4 font-display text-xl font-800 uppercase text-[#050807] transition-colors hover:bg-[#c4dcca] disabled:opacity-40">
                    Aggiungi al carrello
                  </button>
                </div>
                )}
              </motion.div>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </main>
  );
}
